// src/components/subcontractors/SubcontractorsPage.jsx
import { useState } from 'react';
import { useSubcontractors } from '../../context/SubcontractorContext';
import SubcontractorDetailPanel from './SubcontractorDetailPanel';
import SubcontractorFormModal from './modals/SubcontractorFormModal';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function getInitials(name = '') {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

const STATUS_STYLES = {
  active: {
    bg: 'bg-[#10B981]/10',
    text: 'text-[#10B981]',
    border: 'border-[#10B981]/30',
    label: 'Active',
  },
  inactive: {
    bg: 'bg-[#F59E0B]/10',
    text: 'text-[#F59E0B]',
    border: 'border-[#F59E0B]/30',
    label: 'Inactive',
  },
  blacklisted: {
    bg: 'bg-[#EF4444]/10',
    text: 'text-[#EF4444]',
    border: 'border-[#EF4444]/30',
    label: 'Blacklisted',
  },
};

const STATUS_FILTERS = ['All', 'Active', 'Inactive'];

// ─── Component ────────────────────────────────────────────────────────────────

// eslint-disable-next-line no-unused-vars
export default function SubcontractorsPage({ onNavigate }) {
  const { subcontractors, getSubJobs, getJobPaymentSummary } = useSubcontractors();

  const [selectedSubId, setSelectedSubId] = useState(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [newModal, setNewModal] = useState(false);

  // Derive selected sub from context so it stays fresh after edits
  const selectedSub = selectedSubId
    ? subcontractors.find((s) => s.id === selectedSubId) ?? null
    : null;

  // ─── Filtering ──────────────────────────────────────────────────────────────

  const filtered = subcontractors
    .filter((sub) => {
      if (statusFilter === 'Active') return sub.status === 'active';
      if (statusFilter === 'Inactive')
        return sub.status === 'inactive' || sub.status === 'blacklisted';
      return true;
    })
    .filter((sub) => {
      if (!search.trim()) return true;
      const q = search.toLowerCase();
      return (
        (sub.name ?? '').toLowerCase().includes(q) ||
        (sub.company ?? '').toLowerCase().includes(q)
      );
    })
    .sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''));

  // ─── Per-item payment info ───────────────────────────────────────────────────

  function getItemPaymentInfo(sub) {
    const jobs = getSubJobs(sub.id) ?? [];
    const activeJobs = jobs.filter(
      (j) => j.status === 'scheduled' || j.status === 'in_progress',
    );
    const outstanding = activeJobs.reduce((sum, job) => {
      const summary = getJobPaymentSummary(job);
      return sum + (summary.amountOwed > 0 ? summary.amountOwed : 0);
    }, 0);
    return { activeCount: activeJobs.length, outstanding };
  }

  // ─── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="flex h-full bg-gray-50 dark:bg-[#0A1520]">
      {/* ── Left column — list panel ─────────────────────────────────────────── */}
      <div
        className={`${
          selectedSub ? 'hidden md:flex' : 'flex'
        } w-full md:w-80 flex-shrink-0 flex-col border-r border-gray-200 dark:border-[#1E3048] h-full`}
      >
        {/* Header */}
        <div className="px-4 py-4 border-b border-gray-200 dark:border-[#1E3048] flex items-center justify-between gap-3">
          <div>
            <h1 className="text-[var(--wm-text-heading)] font-semibold text-[#0F1923] dark:text-[#F8FAFE]">Subcontractors</h1>
            <p className="text-[var(--wm-text-meta)] text-[#64748B] dark:text-[#7D93AE]">{subcontractors.length} total</p>
          </div>
          <button
            onClick={() => setNewModal(true)}
            className="wm-btn-primary"
          >
            Add Subcontractor
          </button>
        </div>

        {/* Search + filter bar */}
        <div className="px-3 py-2 border-b border-gray-200 dark:border-[#1E3048] space-y-2">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search subcontractors…"
            aria-label="Search subcontractors"
            className="w-full bg-white dark:bg-[#0F1923] border border-gray-200 dark:border-[#243348] rounded-lg px-3 py-1.5 text-[var(--wm-text-body)] text-[#0F1923] dark:text-[#F8FAFE] placeholder:text-[#94A3B8] dark:placeholder:text-[#4A6380] focus:outline-none focus:border-[#2E8BF0]"
          />
          <div className="flex gap-1">
            {STATUS_FILTERS.map((f) => (
              <button
                key={f}
                onClick={() => setStatusFilter(f)}
                className={`px-2 py-1 text-[var(--wm-text-ui)] rounded-md transition-colors ${
                  statusFilter === f
                    ? 'bg-[#2E8BF022] text-[#2E8BF0]'
                    : 'text-[#64748B] dark:text-[#7D93AE] hover:text-[#0F1923] dark:hover:text-[#F8FAFE]'
                }`}
              >
                {f}
              </button>
            ))}
          </div>
        </div>

        {/* Scrollable list */}
        <div className="overflow-y-auto flex-1">
          {filtered.length === 0 ? (
            <p className="text-center text-[#64748B] dark:text-[#7D93AE] text-[var(--wm-text-body)] py-10 px-4">
              No subcontractors match your search.
            </p>
          ) : (
            filtered.map((sub) => {
              const isSelected = sub.id === selectedSubId;
              const statusStyle = STATUS_STYLES[sub.status] ?? STATUS_STYLES.inactive;
              const { activeCount, outstanding } = getItemPaymentInfo(sub);

              return (
                <button
                  key={sub.id}
                  onClick={() => setSelectedSubId(sub.id)}
                  aria-label={`View details for ${sub.name}`}
                  className={`w-full px-4 py-3 flex gap-3 items-start hover:bg-gray-100 dark:hover:bg-[#1B2A3E] transition-colors text-left ${
                    isSelected
                      ? 'bg-gray-100 dark:bg-[#1B2A3E] border-l-2 border-[#2E8BF0]'
                      : 'border-l-2 border-transparent'
                  }`}
                >
                  {/* Avatar */}
                  <div
                    className="w-9 h-9 rounded-full flex items-center justify-center text-white font-bold text-[var(--wm-text-caption)] flex-shrink-0"
                    style={{ background: sub.avatarColor ?? '#2E8BF0' }}
                  >
                    {getInitials(sub.name)}
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <p className="text-[var(--wm-text-body)] font-medium text-[#0F1923] dark:text-[#F8FAFE] truncate">{sub.name}</p>
                    {sub.company && (
                      <p className="text-[var(--wm-text-meta)] text-[#64748B] dark:text-[#7D93AE] truncate">{sub.company}</p>
                    )}

                    {/* Status + specialty count */}
                    <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                      <span
                        className={`text-[var(--wm-text-caption)] px-1.5 py-0.5 rounded-full border ${statusStyle.bg} ${statusStyle.text} ${statusStyle.border}`}
                      >
                        {statusStyle.label}
                      </span>
                      {sub.specialties && sub.specialties.length > 0 && (
                        <span className="text-[var(--wm-text-meta)] text-[#64748B] dark:text-[#7D93AE]">
                          · {sub.specialties.length} {sub.specialties.length === 1 ? 'specialty' : 'specialties'}
                        </span>
                      )}
                    </div>

                    {/* Payment summary row */}
                    {(activeCount > 0 || outstanding > 0) && (
                      <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                        {activeCount > 0 && (
                          <span className="text-[var(--wm-text-meta)] text-[#64748B] dark:text-[#7D93AE]">
                            {activeCount} active {activeCount === 1 ? 'job' : 'jobs'}
                          </span>
                        )}
                        {outstanding > 0 && (
                          <span className="text-[var(--wm-text-meta)] text-[#F59E0B]">
                            ${outstanding.toLocaleString()} owed
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </button>
              );
            })
          )}
        </div>
      </div>

      {/* ── Right column — detail panel (desktop) ────────────────────────────── */}
      <div className="flex-1 hidden md:flex flex-col bg-white dark:bg-[#111C27]">
        <SubcontractorDetailPanel sub={selectedSub} onClose={() => setSelectedSubId(null)} />
      </div>

      {/* ── Mobile full-screen overlay ───────────────────────────────────────── */}
      {selectedSub && (
        <div className="absolute inset-0 z-10 bg-white dark:bg-[#111C27] md:hidden flex flex-col">
          <SubcontractorDetailPanel sub={selectedSub} onClose={() => setSelectedSubId(null)} />
        </div>
      )}

      {/* ── New subcontractor modal ───────────────────────────────────────────── */}
      {newModal && (
        <SubcontractorFormModal sub={null} onClose={() => setNewModal(false)} />
      )}
    </div>
  );
}
